import type { TableColumnStaticProps } from '@/types/tableTypes';
import { entityModel, isSearchableEntity } from '@/composables/entityColumns';

/**
 * Field grouping for the search inspectors. The inspector shows all of an
 * entity's fields (Column Settings only customizes the master-list cards),
 * organized into labeled sections. It leaves out the related fields the
 * search document never holds, {@see isMissingFromSearchDocument}.
 *
 * Location Zone Player gets a hand-curated taxonomy; every other entity falls
 * back to a generic by-kind grouping (Details / Related / Dates).
 */
export interface InspectorFieldGroup {
    title: string;
    columns: TableColumnStaticProps[];
}

/**
 * Relation columns that are flattening sources only — the API never returns
 * them on search rows, so showing them would always render an empty value.
 */
const LOCATION_ZONE_PLAYER_OMITTED_FIELDS = ['player', 'location_zone_player_data'];

/**
 * Ordered taxonomy for Location Zone Player. Fields keep the order listed
 * here; anything models.json grows later that isn't listed lands in "Other"
 * so new fields never silently disappear.
 */
const LOCATION_ZONE_PLAYER_GROUPS: ReadonlyArray<{ title: string; fields: string[] }> = [
    {
        title: 'Now Playing & Check-in',
        // What is playing leads, then where it sits in the list, then the
        // check-in facts. "Playing" is a flag about the player rather than
        // about the track, so it follows the track/playlist lines instead of
        // heading them.
        fields: [
            'player_data_current_track_code',
            'player_data_current_playlist_name',
            'player_data_playlist_track_number',
            'player_data_playlist_track_total',
            'player_data_playing',
            'player_data_date',
            'player_data_checkin_marker',
        ],
    },
    {
        title: 'Playback Settings',
        fields: [
            'volume',
            'player_data_volume',
            'mono',
            'audio_device',
            'location_zone_player_stream_mode',
            'location_zone_player_stream_mode_id',
            'crossfade_duration',
            'crossfade_type',
            'crossfade_type_id',
            'complete_track',
            'can_skip',
            'can_vote',
            'can_set_volume',
            'client_did_set_volume',
            'client_did_set_muted',
            'set_volume_contact',
            'set_volume_contact_id',
        ],
    },
    {
        title: 'Content & Downloads',
        fields: [
            'last_loaded_date',
            'last_loaded_archive',
            'player_data_last_loaded_date',
            'player_data_last_loaded_archive',
            'player_data_referenced_content_missing',
            'player_data_referenced_content_total',
            'download_start_hour',
            'download_end_hour',
        ],
    },
    {
        title: 'Player Configuration',
        fields: [
            'player_id',
            'player_type',
            'player_server',
            'player_check_interval',
            'player_throttle',
            'player_protocol',
            'player_nrm',
            'player_stream_logging',
            'player_curlcli',
            'player_time_zone',
            'player_use_public_dns_servers',
            'player_alternate_dns_1',
            'player_alternate_dns_2',
        ],
    },
    {
        title: 'Network',
        fields: [
            'player_data_remote_ip',
            'player_data_internal_ip',
            'player_data_mac_address',
            'player_data_lan',
            'player_data_subnet',
            'player_data_gateway',
            'player_data_proxy',
            'player_data_ip_mode',
            'player_data_is_internal',
            'player_data_dns_1',
            'player_data_dns_2',
        ],
    },
    {
        title: 'Device',
        fields: [
            'player_data_model_type',
            'player_data_version',
            'player_data_kernel',
            'player_data_channels',
            'player_data_drive',
            'player_data_disk_usage',
            'player_data_disk_usage_percentage',
            'player_data_disk_size_gb',
            'player_data_time_zone',
        ],
    },
    {
        title: 'Organization',
        // brand_id: the owning zone's denormalized GrayV/TPG brand (SAU-259).
        fields: ['account', 'location_zone_id', 'brand_id', 'tags'],
    },
    {
        title: 'Record',
        // deleted_at: the soft-delete stamp added to the zone and its dependents on the
        // backend; it belongs with the other row-lifecycle timestamps, not in "Other".
        fields: ['created_at', 'updated_at', 'deleted_at'],
    },
];

/**
 * Whether a related field can never have a value on a record page. The page
 * reads the record's search document. models.json lists every relation the
 * model has, but the search document holds only the ones in the model's
 * `_searchable` list. So a related field missing from that list always shows
 * "—", for example an artist's `albums`.
 *
 * Only a field whose type is a model counts. A field with a plain type, such
 * as `display_string`, stays even when `_searchable` does not list it. This
 * only narrows the columns a record page offers. It never adds one. A record
 * type with no search endpoint is left alone.
 */
export function isMissingFromSearchDocument(entityId: string, column: TableColumnStaticProps): boolean {
    if (!isSearchableEntity(entityId) || !isRelationDataType(column.dataType)) return false;
    const searchable = entityModel(entityId)?._searchable;
    return Array.isArray(searchable) && !searchable.includes(column.id);
}

function isRenderableColumn(entityId: string, column: TableColumnStaticProps, excludeFields: string[]): boolean {
    // Custom table-only columns (e.g. the Play button) have no model field to render.
    return !excludeFields.includes(column.id) && column.dataType !== 'display' && !isMissingFromSearchDocument(entityId, column);
}

function groupLocationZonePlayerColumns(
    columns: TableColumnStaticProps[],
    excludeFields: string[]
): InspectorFieldGroup[] {
    const omitted = new Set([...excludeFields, ...LOCATION_ZONE_PLAYER_OMITTED_FIELDS]);
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const claimed = new Set<string>();

    const groups: InspectorFieldGroup[] = [];
    for (const group of LOCATION_ZONE_PLAYER_GROUPS) {
        const groupColumns: TableColumnStaticProps[] = [];
        for (const field of group.fields) {
            claimed.add(field);
            const column = columnsById.get(field);
            if (column && !omitted.has(column.id) && isRenderableColumn('LocationZonePlayer', column, excludeFields)) {
                groupColumns.push(column);
            }
        }
        if (groupColumns.length > 0) {
            groups.push({ title: group.title, columns: groupColumns });
        }
    }

    const leftover = columns.filter(
        (column) =>
            !claimed.has(column.id) &&
            !omitted.has(column.id) &&
            isRenderableColumn('LocationZonePlayer', column, excludeFields)
    );
    if (leftover.length > 0) {
        groups.push({ title: 'Other', columns: leftover });
    }

    return groups;
}

function isRelationDataType(dataType: string): boolean {
    return dataType !== 'Date' && /^[A-Z]/.test(dataType);
}

function groupGenericColumns(
    entityId: string,
    columns: TableColumnStaticProps[],
    excludeFields: string[]
): InspectorFieldGroup[] {
    const renderable = columns.filter((column) => isRenderableColumn(entityId, column, excludeFields));

    const details = renderable.filter(
        (column) => !isRelationDataType(column.dataType) && column.dataType !== 'Date'
    );
    const related = renderable.filter((column) => isRelationDataType(column.dataType));
    const dates = renderable.filter((column) => column.dataType === 'Date');

    return [
        { title: 'Details', columns: details },
        { title: 'Related', columns: related },
        { title: 'Dates', columns: dates },
    ].filter((group) => group.columns.length > 0);
}

export function groupColumnsForInspector(
    entityId: string,
    columns: TableColumnStaticProps[],
    excludeFields: string[] = []
): InspectorFieldGroup[] {
    if (entityId === 'LocationZonePlayer') {
        return groupLocationZonePlayerColumns(columns, excludeFields);
    }

    return groupGenericColumns(entityId, columns, excludeFields);
}
